import React, { useEffect, useState } from 'react';
import { DeskTable, ElementTable, ElementCell, ByView } from '../../components/nucleus/Shelf';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useGroup } from '../../contexts/GroupContext';
import { useMarkAttentionStale } from '../../contexts/NotificationContext';
import { useToast } from '../../components/common';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import { Cell } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import DataTable from '../../components/nucleus/DataTable';
import Inspector, { Band, Facts, Note, useOpen } from '../../components/nucleus/Inspector';
import { useConfirm } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import GroupInspector, { GroupFace, groupActions } from './GroupInspector';
import NewGroup from './NewGroup';
import { useGroupsData, sizeOf, sharing, sharesWords } from './groupsData';
import { realmKind, realmLinesForYou } from '../nucleus/groupRealms';
import './GroupsSection.css';
import Notice from '../../components/nucleus/Notice';
import { Plus } from 'lucide-react';
import { ago, dateOf, fmt } from '../../nucleus/format';

// GROUPS — the groups you are in (each a space of its own) and the ones you
// are invited to. A group's own people and settings live inside it.
export default function GroupsSection() {
    const { section, page, groupId } = usePlace();
    const { switchToPersonal } = useGroup();
    const data = useGroupsData();
    // /groups names no group, but a space remembered from before can still be
    // active on it (GroupContext only trusts addresses it lists). Groups is your
    // own space's section, so standing here is standing in your own space.
    useEffect(() => { if (groupId) switchToPersonal?.(); }, [groupId, switchToPersonal]);
    if (!section) return null;
    return page?.id === 'invitations' ? <InvitationsPage data={data} /> : <YoursPage data={data} />;
}

// the filter rail's facets: where you stand in a group, whether you share
// into it, whether it is encrypted; an invitation's role and who sent it
const GROUP_FACETS = [
    { id: 'role', title: 'You are', of: (g) => (g.role === 'admin' ? 'admin' : 'member'), order: ['admin', 'member'], label: (k) => (k === 'admin' ? 'Admin' : 'Member') },
    { id: 'share', title: 'You share', of: (g) => (sharing(g) ? 'some' : 'none'), order: ['some', 'none'], label: (k) => (k === 'some' ? 'Something' : 'Nothing') },
    { id: 'enc', title: 'Encryption', of: (g) => (g.encrypted ? 'on' : 'off'), order: ['on', 'off'], label: (k) => (k === 'on' ? 'On' : 'Off') },
];
const INVITE_FACETS = [
    { id: 'role', title: 'Role', of: (i) => (i.role === 'admin' ? 'admin' : 'member'), order: ['admin', 'member'], label: (k) => (k === 'admin' ? 'Admin' : 'Member') },
    { id: 'by', title: 'Invited by', of: (i) => i.by || null },
];

const roleWord = (g) => (g.role === 'admin' ? 'Admin' : 'Member');

/** Your groups as a table: each one, your role in it, how many are in it, what
 *  you can do there and what you share with it. */
function GroupTable({ groups, openId, onOpen, actionsOf }) {
    const columns = [
        { id: 'name', label: 'Name', lock: true, min: 180, grow: 2, value: (g) => g.name,
          cell: (g) => (
              <span className="groups-table__who">
                  <span className="groups-table__face"><GroupFace group={g} size={24} /></span>
                  <span className="groups-table__name">{g.name}</span>
              </span>
          ) },
        { id: 'role', label: 'Your role', min: 84, grow: 0, value: roleWord, cell: roleWord },
        { id: 'members', label: 'Members', num: true, min: 76, grow: 0, value: (g) => g.count, cell: (g) => (g.count == null ? '—' : fmt(g.count)) },
        { id: 'realm', label: 'Your realm', min: 110, grow: 1, value: (g) => g.access?.realm || null, cell: (g) => g.access?.realm || '—' },
        { id: 'share', label: 'What you share', min: 130, grow: 1.4, value: (g) => (sharing(g) ? sharesWords(g.share) : null), cell: (g) => sharesWords(g.share) },
    ];
    return (
        <DataTable
            id="groups-yours" label="Your groups" columns={columns} rows={groups} keyOf={(g) => g.id}
            currentKey={openId} onOpen={onOpen} actionsOf={actionsOf}
        />
    );
}

function YoursPage({ data }) {
    const { section, page, go, navigate } = usePlace();
    const toast = useToast();
    const { user } = useAuth();
    const { refreshGroups } = useGroup();
    const [openId, open, close] = useOpen();
    const [making, setMaking] = useState(false);
    const meId = String(user?.id || user?.user_id || '');
    const confirm = useConfirm();
    const ctx = { navigate, toast, meId, refreshGroups, reload: data.reload, close, confirm };
    const current = data.groups.find((g) => g.id === String(openId));
    const waiting = data.invitations;
    const f = useFacets(data.groups, GROUP_FACETS, { text: (g) => `${g.name} ${g.description || ''}`, placeholder: 'Filter groups' });
    const head = {
        title: 'Your groups', count: data.loaded ? f.shown.length : undefined,
        empty: data.loaded ? (f.filtering ? 'Nothing matches the filters.' : 'You are in no groups yet. New group makes one.') : undefined,
    };

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data.loaded ? data.groups.length : undefined}
            verbs={<Verb primary icon={<Plus />} onClick={() => setMaking(true)}>New group</Verb>}
            inspector={current && <GroupInspector key={current.id} group={current} ctx={ctx} onClose={close} />}
        >
            <Faceted id="groups" rail={f.rail} notice={waiting.length > 0 && (
                <Notice
                    title={waiting.length === 1 ? `${waiting[0].name} invited you` : `${waiting.length} groups invited you`}
                    sub={waiting.length > 1 ? waiting.map((i) => i.name).join(', ') : null}
                    action={{ label: 'See invitations', run: () => go('invitations') }}
                />
            )}>
            <ByView
                cards={(
                    <DeskTable top={0} between={40}>
                        <ElementTable {...head}>
                            {f.shown.map((g) => (
                                <Cell key={g.id} actions={groupActions(g, ctx)}>
                                    <ElementCell
                                        mark={<GroupFace group={g} />}
                                        name={g.name}
                                        tag={g.role === 'admin' ? 'ADMIN' : undefined}
                                        // what you can do there, then what you share
                                        note={[g.access?.realm || sizeOf(g.count), sharing(g) ? sharesWords(g.share) : 'nothing'].filter(Boolean).join(' · ')}
                                        title={g.description || g.name}
                                        current={g.id === String(openId)}
                                        onPress={() => open(g.id)}
                                    />
                                </Cell>
                            ))}
                        </ElementTable>
                    </DeskTable>
                )}
                rows={(
                    <DeskTable top={0} between={40}>
                        <TitledSet {...head}>
                            {f.shown.length > 0 && <GroupTable groups={f.shown} openId={openId} onOpen={open} actionsOf={(g) => groupActions(g, ctx)} />}
                        </TitledSet>
                    </DeskTable>
                )}
            />
            </Faceted>
            <NewGroup open={making} onClose={() => setMaking(false)} />
        </NucleusPage>
    );
}

// INVITATIONS: the groups that asked you in. Joining goes through the consent
// page (what you share with the group), which then lands you inside it. One you
// started joining and left there is still waiting: Join finishes it.
function InvitationsPage({ data }) {
    const { section, page, navigate } = usePlace();
    const toast = useToast();
    const { refreshGroups } = useGroup();
    // an invitation answered is one fewer waiting: the Groups badge is read again now
    const stale = useMarkAttentionStale();
    const [openId, open, close] = useOpen();
    const current = data.invitations.find((i) => i.id === String(openId));
    const f = useFacets(data.invitations, INVITE_FACETS, { text: (i) => `${i.name} ${i.by || ''}`, placeholder: 'Filter invitations' });

    const actionsOf = (i) => [
        {
            id: 'join', label: 'Join', primary: true,
            run: async () => {
                const res = await api.respondToInvitation(i.id, 'accept');
                stale();
                if (res?.requires_authorization && res?.redirect_url) {
                    // the consent page lands you in the group when you approve, and back here if you don't
                    navigate(res.redirect_url);
                    return;
                }
                toast.success(`You joined ${i.name}`);
                await refreshGroups?.();
                navigate(`/group/${i.groupId}/graph`);
            },
        },
        {
            id: 'decline', label: 'Decline',
            confirm: { title: `Decline the invitation to ${i.name}?`, body: 'An admin would have to invite you again.', label: 'Decline' },
            run: async () => { await api.respondToInvitation(i.id, 'decline'); stale(); toast.success('Invitation declined'); close(); data.reload(); },
        },
    ];

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={data.loaded ? data.invitations.length : undefined}
            inspector={current && (
                <Inspector
                    key={current.id}
                    face={<GroupFace group={current} size={56} />}
                    tag={current.started ? 'Invitation · started' : `Invitation · as ${current.role === 'admin' ? 'Admin' : 'Member'}`}
                    title={current.name}
                    sub={current.description || undefined}
                    actions={actionsOf(current)}
                    onClose={close}
                >
                    {/* who is in it is not shown: the members list is for members,
                        and the invitation carries no count */}
                    <Band title="The invitation">
                        <Facts items={[
                            { label: 'From', value: current.by || '—' },
                            { label: 'When', value: dateOf(current.at) || '—' },
                            { label: 'Role', value: current.role === 'admin' ? 'Admin' : 'Member' },
                            current.realm && { label: 'Can do', value: current.realm.name },
                        ]} />
                    </Band>
                    {current.started ? (
                        <Band title="You started joining">
                            <Note>You pressed Join{current.startedAt ? ` on ${dateOf(current.startedAt)}` : ''} and left the page where you choose what you share. Nothing was shared and you are not in it yet.</Note>
                            <Note>Join takes you back to that page. Decline ends the invitation.</Note>
                        </Band>
                    ) : (
                        <Band title="If you join">
                            <Note>What you can do in {current.name}: {!current.realm ? 'the realm an admin puts you on'
                                : realmKind(current.realm) ? `${current.realm.name}, which lets you ${realmLinesForYou(current.realm, current.name)[0].replace(/^./, (c) => c.toLowerCase())}`
                                    // a realm an admin made says what it allows in its own words
                                    : `${current.realm.name}${current.realm.description ? ` (${current.realm.description.replace(/\.$/, '')})` : ''}`}. {current.by || 'The admin'} chose it; an admin can change it.</Note>
                            <Note>What you share with it: you choose on the next page, from your own realms. Sharing nothing is fine.</Note>
                        </Band>
                    )}
                </Inspector>
            )}
        >
            {data.loaded && (
                <Faceted id="groups-invitations" rail={f.rail}>
                <DeskTable top={0} between={40}>
                    <ElementTable heads={{ note: 'Invitation', tag: 'Status' }} title="Asked you in" count={f.shown.length} empty={f.filtering ? 'Nothing matches the filters.' : 'No invitations waiting.'}>
                        {f.shown.map((i) => (
                            <Cell key={i.id} actions={actionsOf(i)}>
                                <ElementCell
                                    mark={<GroupFace group={i} />}
                                    name={i.name}
                                    note={i.started ? 'You started joining — finish choosing what you share'
                                        : `${i.by ? `${i.by} invited you` : 'You were invited'} ${ago(i.at, { dateAfter: 30 })}${i.role === 'admin' ? ' · as an admin' : ''}`}
                                    tag={i.started ? 'UNFINISHED' : 'WAITING'}
                                    title={i.description || i.name}
                                    current={i.id === String(openId)}
                                    onPress={() => open(i.id)}
                                />
                            </Cell>
                        ))}
                    </ElementTable>
                </DeskTable>
                </Faceted>
            )}
        </NucleusPage>
    );
}
